import React, { useState, useEffect, useMemo } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Table,
    FormGroup,
    Label,
    Input,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";

import Breadcrumb from "../../components/Common/Breadcrumb";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getPendingBilledServicesReport } from "../../components/Api/GmsPendingBilledServicesReport";
import { hasPagePermissionWithAdmin } from "../../components/Permission/PermissionService";

const BilledServicesReportPage = () => {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsBilledServicesReport');

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [branches, setBranches] = useState([]);
    const [customers, setCustomers] = useState([]);

    const [filters, setFilters] = useState({
        companyId: null,
        branchId: null,
        customerId: null,
    });

    const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);

    useEffect(() => {
        if (!hasPermission) {
            window.location.href = "/Dashboard";
        }
    }, [hasPermission]);

    // Load initial data
    useEffect(() => {
        const init = async () => {
            try {
                // Get user's context from localStorage (matching TaxInvoiceReportPage logic)
                const userCompanyId = localStorage.getItem("userCompanyId") ? Number(localStorage.getItem("userCompanyId")) : null;
                const userBranchId = localStorage.getItem("userBranchId") ? Number(localStorage.getItem("userBranchId")) : null;

                const [comps, brs, custs] = await Promise.all([
                    getBusinesses({ business_Active: true }),
                    getBranches({ branch_Active: true }),
                    getCustomers({}),
                ]);

                setCompanies(comps.map(c => ({ value: c.business_Id, label: c.business_NameEn })));
                setBranches(brs || []); // Store raw branches to filter later
                setCustomers(custs.map(c => ({ value: c.customer_Id, label: `${c.customer_NameEn} (${c.customer_Code || c.customer_CardNo})` })));

                const initialFilters = {
                    companyId: userCompanyId,
                    branchId: userBranchId,
                    customerId: null,
                };
                setFilters(initialFilters);

                if (userCompanyId && userBranchId) {
                    loadReport(initialFilters);
                }
            } catch (error) {
                console.error("Error initializing page:", error);
            }
        };
        init();
    }, []);

    const searchCustomersManually = async (inputValue) => {
        // Trim spaces from the search text to handle cases like "052 422 5719"
        const cleanedInput = inputValue ? inputValue.replace(/\s+/g, '') : '';
        const originalInput = inputValue ? inputValue.trim() : '';

        if (!originalInput || originalInput.length < 3) {
            setCustomerSearchOptions([]);
            return;
        }

        try {
            setCustomerSearchLoading(true);
            // Try searching with the cleaned input (no spaces)
            const response = await searchCustomers(cleanedInput, 1, 20);

            let formattedOptions = response.items.map(customer => ({
                value: customer.customer_Id,
                label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
                customer: customer
            }));

            // If no results with cleaned input, try with original input (optional, but safer)
            if (formattedOptions.length === 0 && cleanedInput !== originalInput) {
                const responseOriginal = await searchCustomers(originalInput, 1, 20);
                formattedOptions = responseOriginal.items.map(customer => ({
                    value: customer.customer_Id,
                    label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
                    customer: customer
                }));
            }

            setCustomerSearchOptions(formattedOptions);
        } catch (error) {
            console.error('Error searching customers:', error);
            setCustomerSearchOptions([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    };

    const loadReport = async (currentFilters = filters) => {
        setLoading(true);
        try {
            const reportData = await getPendingBilledServicesReport(currentFilters);
            setData(reportData);
        } catch (error) {
            Swal.fire("Error", "Failed to load report data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilter = () => {
        loadReport();
    };

    const handleClearFilter = () => {
        const userCompanyId = localStorage.getItem("userCompanyId") ? Number(localStorage.getItem("userCompanyId")) : null;
        const userBranchId = localStorage.getItem("userBranchId") ? Number(localStorage.getItem("userBranchId")) : null;

        const cleared = {
            companyId: userCompanyId,
            branchId: userBranchId,
            customerId: null,
        };
        setFilters(cleared);
        setSelectedCustomerOption(null);
        setCustomerSearchOptions([]);
        loadReport(cleared);
    };

    const filteredBranches = useMemo(() => {
        if (!filters.companyId) return [];
        return branches
            .filter(b => b.branch_Buisness_Id === filters.companyId || b.branch_BusinessId === filters.companyId)
            .map(branch => ({
                value: branch.branch_Id,
                label: branch.branch_NameEn
            }));
    }, [branches, filters.companyId]);

    const exportIndividual = (row) => {
        // Group all services for this customer from the data
        const customerServices = data.filter(d => d.customerId === row.customerId);

        const wsData = [
            ["PENDING BILLED SERVICES REPORT"],
            [],
            ["Customer No:", row.customerNo],
            ["Customer Name:", row.customerName],
            ["Contact No:", row.contactNo],
            [],
            ["Sl", "Purchase Date", "Package/Series Of Package", "Service Name", "Amount Paid", "Pending (Nos)"]
        ];

        customerServices.forEach((s, idx) => {
            wsData.push([
                idx + 1,
                s.purchaseDate ? new Date(s.purchaseDate).toLocaleDateString() : "N/A",
                s.packageName,
                s.serviceName,
                s.amountPaid || 0,
                s.pendingQty
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Set column widths
        ws["!cols"] = [
            { wch: 5 },  // Sl
            { wch: 15 }, // Purchase Date
            { wch: 30 }, // Package
            { wch: 30 }, // Service
            { wch: 15 }, // Amount Paid
            { wch: 15 }  // Pending
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Customer Report");
        XLSX.writeFile(wb, `PendingServices_${row.customerNo}_${row.customerName.replace(/\s+/g, '_')}.xlsx`);
    };

    const exportFullReport = () => {
        if (data.length === 0) {
            Swal.fire("Warning", "No data to export", "warning");
            return;
        }

        const wsData = [
            ["Sl", "CustomerNo", "CustomerName", "Contact No", "Purchase Date", "Package/Series Of Package", "Service Name", "Amount Paid", "Pending (Nos)"]
        ];

        data.forEach((row, idx) => {
            wsData.push([
                idx + 1,
                row.customerNo,
                row.customerName,
                row.contactNo,
                row.purchaseDate ? new Date(row.purchaseDate).toLocaleDateString() : "N/A",
                row.packageName,
                row.serviceName,
                row.amountPaid || 0,
                row.pendingQty
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Full Report");
        XLSX.writeFile(wb, `PendingBilledServices_FullReport.xlsx`);
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumb title="Reports" breadcrumbItem="Pending Billed Services" />

                <Card>
                    <CardHeader>
                        <h5 className="card-title mb-0">Filters</h5>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Company</Label>
                                    <Select
                                        value={companies.find(c => c.value === filters.companyId)}
                                        onChange={(opt) => setFilters({ ...filters, companyId: opt ? opt.value : null, branchId: null })}
                                        options={companies}
                                        isClearable
                                        placeholder="Select Company"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Branch</Label>
                                    <Select
                                        value={branches.find(b => b.value === filters.branchId)}
                                        onChange={(opt) => setFilters({ ...filters, branchId: opt ? opt.value : null })}
                                        options={filteredBranches}
                                        isClearable
                                        placeholder="Select Branch"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Label>Customer</Label>
                                    <Select
                                        value={selectedCustomerOption}
                                        onChange={(opt) => {
                                            setSelectedCustomerOption(opt);
                                            setFilters({ ...filters, customerId: opt ? opt.value : null });
                                        }}
                                        options={customerSearchOptions}
                                        isClearable
                                        isSearchable
                                        isLoading={customerSearchLoading}
                                        placeholder="Type 3+ chars to search..."
                                        filterOption={() => true}
                                        onInputChange={(inputValue) => {
                                            if (searchTimeout) clearTimeout(searchTimeout);
                                            const timeout = setTimeout(() => searchCustomersManually(inputValue), 300);
                                            setSearchTimeout(timeout);
                                            return inputValue;
                                        }}
                                        noOptionsMessage={() => "Type at least 3 characters to search"}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3} className="d-flex align-items-end mb-3">
                                <Button color="primary" className="me-2" onClick={handleApplyFilter}>
                                    Apply
                                </Button>
                                <Button color="secondary" onClick={handleClearFilter}>
                                    Clear
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">Report Data</h5>
                        <Button color="success" size="sm" onClick={exportFullReport}>
                            <Download size={16} className="me-1" /> Export All
                        </Button>
                    </CardHeader>
                    <CardBody>
                        {loading ? (
                            <div className="text-center p-5">
                                <ClipLoader size={50} color="#556ee6" />
                                <p className="mt-2">Loading data...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table bordered hover>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Sl</th>
                                            <th>CustomerNo</th>
                                            <th>CustomerName</th>
                                            <th>Contact No</th>
                                            <th>Purchase Date</th>
                                            <th>Package/Series Of Package</th>
                                            <th>Service Name</th>
                                            <th>Amount Paid</th>
                                            <th>Pending (Nos)</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length > 0 ? (
                                            data.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td>{row.customerNo}</td>
                                                    <td>{row.customerName}</td>
                                                    <td>{row.contactNo}</td>
                                                    <td>{row.purchaseDate ? new Date(row.purchaseDate).toLocaleDateString() : "N/A"}</td>
                                                    <td>{row.packageName}</td>
                                                    <td>{row.serviceName}</td>
                                                    <td className="text-end">{row.amountPaid?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                    <td>{row.pendingQty}</td>
                                                    <td>
                                                        <Button
                                                            color="link"
                                                            className="p-0"
                                                            title="Export Customer Details"
                                                            onClick={() => exportIndividual(row)}
                                                        >
                                                            <Download size={20} color="#34c38f" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    No pending billed services found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default BilledServicesReportPage;
